// ABOUTME: Presentational list of node references drawn like the data-table's link cells.
// ABOUTME: The first as a chip, the others in a "+N" chip that opens a menu on hover; without a click handler it is read-only.

import { Box, Chip, LinkIcon, Menu, MenuItem, Stack, Typography } from '@ds/coolshop';
import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

export type LinkChipItem = { key: string; label: string };

/** `index` is the position of the clicked item in `items`. */
export type LinkChipClickHandler = (e: MouseEvent<HTMLElement>, index: number) => void;

function MoreLinksChip({
    others,
    hiddenCount,
    onItemClick,
    disabled,
}: {
    others: LinkChipItem[];
    hiddenCount: number;
    onItemClick?: LinkChipClickHandler;
    disabled?: boolean;
}) {
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const menuPaperRef = useRef<HTMLElement | null>(null);

    // The menu opens on hover, so it closes as soon as the pointer leaves it (with some slack
    // around the paper, to cross from the chip to the menu).
    useEffect(() => {
        if (!menuAnchorEl) return undefined;
        const handleMouseMove = (e: globalThis.MouseEvent) => {
            const paper = menuPaperRef.current;
            if (!paper) return;
            const rect = paper.getBoundingClientRect();
            const inZone =
                e.clientX >= rect.left - 10 &&
                e.clientX <= rect.right + 10 &&
                e.clientY >= rect.top - 22 &&
                e.clientY <= rect.bottom + 22;
            if (!inZone) setMenuAnchorEl(null);
        };
        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, [menuAnchorEl]);

    return (
        <>
            <Chip
                size="small"
                label={`+ ${hiddenCount}`}
                disabled={disabled}
                onMouseEnter={e => setMenuAnchorEl(e.currentTarget)}
            />
            {/* The menu is portalled but its events still bubble to the row: they must
            not trigger the row click. */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <span
                style={{ display: 'contents' }}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
            >
                <Menu
                    open={Boolean(menuAnchorEl)}
                    anchorEl={menuAnchorEl}
                    onClose={() => setMenuAnchorEl(null)}
                    maxHeight={300}
                    // The DS Menu types omit these MUI props but forwards them: the paper
                    // ref drives the hover-close, as in the package's link cell.
                    {...({
                        PaperProps: {
                            ref: menuPaperRef,
                            style: { overflow: 'visible', maxWidth: '300px' },
                        },
                        disableRestoreFocus: true,
                        disableAutoFocusItem: true,
                    } as object)}
                >
                    <Box minWidth="max-content">
                        {others.map((item, i) =>
                            onItemClick ? (
                                <MenuItem
                                    key={item.key}
                                    icon={<LinkIcon fontSize="small" />}
                                    onClick={e => {
                                        setMenuAnchorEl(null);
                                        // The first item is the chip outside the menu.
                                        onItemClick(e, i + 1);
                                    }}
                                >
                                    {item.label}
                                </MenuItem>
                            ) : (
                                // A plain row: a MenuItem would hover and point like an action.
                                <Stack
                                    key={item.key}
                                    direction="row"
                                    gap={1.5}
                                    alignItems="center"
                                    px={2}
                                    py={0.75}
                                >
                                    <LinkIcon fontSize="small" color="action" />
                                    <Typography variant="body2">{item.label}</Typography>
                                </Stack>
                            ),
                        )}
                    </Box>
                </Menu>
            </span>
        </>
    );
}

export function LinkChipList({
    items,
    totalCount = items.length,
    onItemClick,
    disabled,
}: {
    items: LinkChipItem[];
    /** all the references, when `items` holds only the first ones */
    totalCount?: number;
    onItemClick?: LinkChipClickHandler;
    disabled?: boolean;
}) {
    const [first, ...others] = items;
    if (!first) {
        return null;
    }

    return (
        <Stack direction="row" gap={1} alignItems="center">
            <Box maxWidth={totalCount > 1 ? '80%' : '95%'}>
                <Chip
                    size="small"
                    icon={<LinkIcon />}
                    label={first.label}
                    disabled={disabled}
                    onClick={onItemClick ? e => onItemClick(e, 0) : undefined}
                />
            </Box>
            {/* Mounted only for several references: a single-reference cell keeps no menu state. */}
            {totalCount > 1 && (
                <MoreLinksChip
                    others={others}
                    hiddenCount={totalCount - 1}
                    onItemClick={onItemClick}
                    disabled={disabled}
                />
            )}
        </Stack>
    );
}
